// App root: routing, code gate, language state, week state.

import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useCode } from './lib/useCode';
import type { Lang } from './lib/i18n';
import { CodeGate } from './components/CodeGate';
import { Header } from './components/Header';
import { Radar } from './routes/Radar';
import { Pricing } from './routes/Pricing';
import { Benchmark } from './routes/Benchmark';
import { PriceChanges } from './routes/PriceChanges';
import { Provider } from './routes/Provider';
import { Digest } from './routes/Digest';
import { api } from './lib/api';

function AppInner() {
  const { code, setCode, isValid } = useCode();
  const [lang, setLang] = useState<Lang>('tr'); // Default: Turkish
  const [currentWeek, setCurrentWeek] = useState<number | null>(null);
  const [isoDate, setIsoDate] = useState<string | null>(null);

  // Load current week from latest snapshot once auth is confirmed
  useEffect(() => {
    if (isValid !== true) return;
    if (currentWeek) return; // Already loaded

    api.snapshots.latest()
      .then((snapshot) => {
        setCurrentWeek(snapshot.week_number);
        setIsoDate(snapshot.iso_date);
      })
      .catch(() => {
        // No snapshots yet — set a sensible default
        const now = new Date();
        const year = now.getFullYear();
        const weekNum = getISOWeek(now);
        setCurrentWeek(year * 100 + weekNum);
        setIsoDate(now.toISOString().slice(0, 10));
      });
  }, [isValid, currentWeek]);

  function toggleLang() {
    setLang((prev) => (prev === 'tr' ? 'en' : 'tr'));
  }

  function handleWeekChange(week: number) {
    setCurrentWeek(week);
    setIsoDate(null); // Will be resolved from API on next load
  }

  // Show code gate if not yet valid or still loading
  if (isValid === false || (isValid === null && !code)) {
    return <CodeGate lang={lang} onValidCode={setCode} />;
  }

  // Loading state while validating
  if (isValid === null && code) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-neutral-500 text-sm">...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header
        lang={lang}
        onLangToggle={toggleLang}
        currentWeek={currentWeek}
        isoDate={isoDate}
        onWeekChange={handleWeekChange}
        code={code}
      />
      <main>
        <Routes>
          <Route path="/" element={<Radar lang={lang} currentWeek={currentWeek} code={code} />} />
          <Route path="/pricing" element={<Pricing lang={lang} currentWeek={currentWeek} code={code} />} />
          <Route path="/benchmark" element={<Benchmark lang={lang} currentWeek={currentWeek} code={code} />} />
          <Route path="/price-changes" element={<PriceChanges lang={lang} currentWeek={currentWeek} code={code} />} />
          <Route path="/providers/:id" element={<Provider lang={lang} currentWeek={currentWeek} code={code} />} />
          <Route path="/digest/:week" element={<Digest lang={lang} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayOfWeek = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
